/**
 * Agent Models Tab Component
 * 
 * Integrates model selection with the agent form.
 * Fetches available models from API and populates form fields.
 */

import { Card, Icon } from '@/components/ui';
import { useI18n } from '@/i18n';
import { ModelSelect } from './ModelSelect';
import { useAvailableModels } from '../hooks/useAvailableModels';
import type { AvailableModel } from '../types';
import type { ModelConfig as SchemaModelConfig, ModelsConfig as SchemaModelsConfig } from '../schemas';

// =============================================================================
// Types
// =============================================================================

interface AgentModelsTabProps {
  modelsConfig: SchemaModelsConfig;
  onModelChange: (
    slot: 'main' | 'router' | 'tools' | 'fallback',
    config: SchemaModelConfig | null
  ) => void;
}

// =============================================================================
// Helper Functions
// =============================================================================

// Use a simpler type for the config parameter since we're reading from ModelsConfig
interface SimpleModelConfig {
  provider: string;
  model: string;
  catalog_slug?: string | null;
}

/**
 * Find a model by matching the canonical catalog slug from ModelsConfig.
 * Catalog-driven (ISS-447): matches catalog_slug first, then model_id (both
 * are canonical slugs from the model catalog).
 */
function findModelByConfig(
  models: AvailableModel[],
  config: SimpleModelConfig | null | undefined
): string | undefined {
  if (!config?.model) return undefined;

  // Strategy 1: match by canonical catalog slug
  const configCatalog = (config as { catalog_slug?: string | null }).catalog_slug;
  if (configCatalog) {
    const byCatalog = models.find(
      (m) => (m.catalog_slug ?? m.model_id) === configCatalog
    );
    if (byCatalog) return byCatalog.id;
  }

  // Strategy 2: match by model_id (canonical slug)
  const byModelId = models.find((m) => m.model_id === config.model);
  if (byModelId) return byModelId.id;

  return undefined;
}

/**
 * Convert AvailableModel to ModelConfig for the form.
 * Catalog-driven (ISS-447): stores the canonical catalog slug — the serving
 * provider is resolved by policy at runtime (ADR-041/ADR-042), not pinned
 * in the agent config.
 */
function modelToConfig(model: AvailableModel): SchemaModelConfig {
  return {
    provider: 'azure-foundry',
    model: model.model_id,
    catalog_slug: model.catalog_slug ?? model.model_id,
    // Default temperature of 0.3 provides a balance between creativity and consistency.
    // This is the platform's recommended default for general-purpose agents.
    // Users can adjust this in the agent's advanced settings if needed.
    temperature: 0.3,
  };
}

// =============================================================================
// Component
// =============================================================================

export function AgentModelsTab({ modelsConfig, onModelChange }: AgentModelsTabProps) {
  const { t } = useI18n();
  const { data: models, isLoading, error } = useAvailableModels();

  // Filter models by category
  const mainModels = models?.filter((m) => m.category === 'main') || [];
  const routerModels = models?.filter((m) => m.category === 'router') || [];
  const toolsModels = models?.filter((m) => m.category === 'tools') || [];
  
  // Use main models for fallback as well
  const fallbackModels = mainModels;

  // Get current selected model IDs from config
  const selectedMain = findModelByConfig(mainModels, modelsConfig.main);
  const selectedRouter = findModelByConfig(routerModels, modelsConfig.router);
  const selectedTools = findModelByConfig(toolsModels, modelsConfig.tools);
  const selectedFallback = findModelByConfig(fallbackModels, modelsConfig.fallback);

  // Handle model selection
  const handleModelChange = (
    slot: 'main' | 'router' | 'tools' | 'fallback',
    modelId: string | undefined
  ) => {
    if (!modelId) {
      onModelChange(slot, null);
      return;
    }
    
    const allModels = models || [];
    const selectedModel = allModels.find((m) => m.id === modelId);
    
    if (selectedModel) {
      onModelChange(slot, modelToConfig(selectedModel));
    }
  };

  // Loading State
  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {[1, 2, 3, 4].map((i) => (
          <Card key={i} className="p-4 animate-pulse">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 bg-gray-200 dark:bg-gray-700 rounded-lg" />
              <div className="flex-1">
                <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-24 mb-2" />
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-48" />
              </div>
            </div>
            <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded" />
          </Card>
        ))}
      </div>
    );
  }

  // Error State - Fall back to showing empty state
  if (error) {
    return (
      <Card className="p-6">
        <div className="text-center py-8">
          <Icon name="alert-circle" size={48} className="mx-auto mb-4 text-red-400" />
          <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">
            Failed to load models
          </h4>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Could not fetch available models from the platform. Please check LLM provider configuration.
          </p>
        </div>
      </Card>
    );
  }

  // No models available
  if (mainModels.length === 0) {
    return (
      <Card className="p-6">
        <div className="text-center py-8">
          <Icon name="zap" size={48} className="mx-auto mb-4 text-gray-300 dark:text-gray-600" />
          <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">
            No models available
          </h4>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Configure LLM providers in Settings → LLM Configuration to enable model selection.
          </p>
        </div>
      </Card>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {/* Main Model */}
      <Card className="p-4">
        <div className="flex items-start gap-3 mb-4">
          <div className="p-2 rounded-lg bg-blue-50 dark:bg-blue-900">
            <Icon name="zap" size={20} className="text-blue-600 dark:text-blue-400" />
          </div>
          <div>
            <h4 className="font-medium text-gray-900 dark:text-gray-100">
              {t('admin.agents.editor.models.mainModel')}
              <span className="text-red-500 ml-1">*</span>
            </h4>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {t('admin.agents.editor.models.mainModelDesc')}
            </p>
          </div>
        </div>
        <ModelSelect
          value={selectedMain}
          onChange={(id) => handleModelChange('main', id)}
          models={mainModels}
          category="main"
          required
          placeholder="Select main model..."
        />
      </Card>

      {/* Router Model */}
      <Card className="p-4">
        <div className="flex items-start gap-3 mb-4">
          <div className="p-2 rounded-lg bg-purple-50 dark:bg-purple-900">
            <Icon name="activity" size={20} className="text-purple-600 dark:text-purple-400" />
          </div>
          <div>
            <h4 className="font-medium text-gray-900 dark:text-gray-100">
              {t('admin.agents.editor.models.routerModel')}
            </h4>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {t('admin.agents.editor.models.routerModelDesc')}
            </p>
          </div>
        </div>
        <ModelSelect
          value={selectedRouter}
          onChange={(id) => handleModelChange('router', id)}
          models={routerModels.length > 0 ? routerModels : mainModels}
          category={routerModels.length > 0 ? 'router' : 'main'}
          placeholder="None (uses main model)"
        />
      </Card>

      {/* Tools Model */}
      <Card className="p-4">
        <div className="flex items-start gap-3 mb-4">
          <div className="p-2 rounded-lg bg-green-50 dark:bg-green-900">
            <Icon name="settings" size={20} className="text-green-600 dark:text-green-400" />
          </div>
          <div>
            <h4 className="font-medium text-gray-900 dark:text-gray-100">
              {t('admin.agents.editor.models.toolsModel')}
            </h4>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {t('admin.agents.editor.models.toolsModelDesc')}
            </p>
          </div>
        </div>
        <ModelSelect
          value={selectedTools}
          onChange={(id) => handleModelChange('tools', id)}
          models={toolsModels.length > 0 ? toolsModels : mainModels}
          category={toolsModels.length > 0 ? 'tools' : 'main'}
          placeholder="None (uses main model)"
        />
      </Card>

      {/* Fallback Model */}
      <Card className="p-4">
        <div className="flex items-start gap-3 mb-4">
          <div className="p-2 rounded-lg bg-orange-50 dark:bg-orange-900">
            <Icon name="shield" size={20} className="text-orange-600 dark:text-orange-400" />
          </div>
          <div>
            <h4 className="font-medium text-gray-900 dark:text-gray-100">
              {t('admin.agents.editor.models.fallbackModel')}
            </h4>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {t('admin.agents.editor.models.fallbackModelDesc')}
            </p>
          </div>
        </div>
        <ModelSelect
          value={selectedFallback}
          onChange={(id) => handleModelChange('fallback', id)}
          models={fallbackModels}
          category="main"
          placeholder="None (no fallback)"
        />
      </Card>
    </div>
  );
}
